import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "cn";
import type * as React from "react";
import { useMobileVisualViewportAdjust } from "../hooks/use-mobile-visual-viewport-adjust";
import type {
  DialogCloseProps,
  DialogContentProps,
  DialogDescriptionProps,
  DialogFooterProps,
  DialogHeaderProps,
  DialogOverlayProps,
  DialogPortalProps,
  DialogProps,
  DialogTitleProps,
} from "./types";
import { dialogSizes, footerLayouts } from "./types";

// Inline SVG close icon to avoid an icon dependency.
function CloseIcon({ size = 20 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <title>Close</title>
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  );
}

export function Dialog({ ...props }: DialogProps) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

export function DialogTrigger({
  ...props
}: DialogPrimitive.DialogTriggerProps) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

export function DialogPortal({ ...props }: DialogPortalProps) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

export function DialogClose({ ...props }: DialogCloseProps) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

export function DialogOverlay({
  className,
  showOverlay = true,
  ...props
}: DialogOverlayProps) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        /* sorted */
        "fixed inset-0 z-50",
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        {
          "bg-black/24 dark:bg-black/64": showOverlay,
        },
        className,
      )}
      {...props}
    />
  );
}

export function DialogContent({
  className,
  children,
  size = "md",
  showCloseButton = true,
  showOverlay = true,
  style,
  onPointerDownOutside,
  ...props
}: DialogContentProps) {
  const { bottomPx, maxVisibleHeightPx } = useMobileVisualViewportAdjust();

  const handlePointerDownOutside: DialogContentProps["onPointerDownOutside"] = (
    event,
  ) => {
    const target = event.target as HTMLElement | null;
    if (target?.closest?.("[data-sonner-toaster]")) {
      event.preventDefault();
    }
    onPointerDownOutside?.(event);
  };

  const viewportStyle: React.CSSProperties | undefined =
    bottomPx > 0
      ? {
          bottom: bottomPx,
          ...(maxVisibleHeightPx != null
            ? { maxHeight: maxVisibleHeightPx }
            : {}),
        }
      : undefined;

  const mergedStyle =
    viewportStyle != null ? { ...style, ...viewportStyle } : style;

  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay showOverlay={showOverlay} />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        data-inline-edit-keep-open=""
        onPointerDownOutside={handlePointerDownOutside}
        style={mergedStyle}
        className={cn(
          /* sorted */
          "fixed left-1/2 z-50",
          "grid gap-0",
          "w-full max-h-[calc(100%-4rem)] max-w-[calc(100%)]",
          "p-0",
          "bg-bg-surface-elevate-low rounded-3xl shadow-2xl",
          "translate-x-[-50%] translate-y-0",
          "duration-200",
          // Default (desktop): centered fade + zoom. Kept on the un-prefixed
          // `data-[state]` utilities because, in this build, base utilities win
          // the cascade over their `sm:`-prefixed counterparts — so the desktop
          // animation must live in the base, not behind `sm:`.
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          "data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0",
          "data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95",
          // Below `sm` (a bare Dialog on a phone): anchor to the bottom and
          // slide up like a sheet. Scoped to `max-sm:` so it never competes with
          // the desktop rules above.
          "max-sm:bottom-0",
          "max-sm:data-[state=open]:slide-in-from-bottom max-sm:data-[state=closed]:slide-out-to-bottom",
          "sm:top-6 sm:max-h-[min(640px,80vh)] sm:translate-y-0",
          dialogSizes[size],
          className,
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            data-test="dialog-close-button"
            className={cn(
              /* sorted */
              "inline-flex absolute top-3 right-3",
              "items-center justify-center",
              "h-8 w-8",
              "text-text-dark-max",
              "cursor-pointer rounded-full outline-none",
              "transition-[color,box-shadow]",
              "hover:bg-bg-dark-min focus:bg-bg-dark-low focus:outline-hidden disabled:pointer-events-none",
              "[&_svg]:pointer-events-none [&_svg]:shrink-0",
            )}
          >
            <CloseIcon />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

export function DialogHeader({ className, ...props }: DialogHeaderProps) {
  return (
    <div
      data-slot="dialog-header"
      className={cn(
        /* sorted */
        "flex flex-col gap-2",
        "p-4",
        "text-center",
        "sm:text-left",
        className,
      )}
      {...props}
    />
  );
}

export function DialogFooter({
  className,
  layout = "default",
  ...props
}: DialogFooterProps) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(footerLayouts[layout], "p-4", className)}
      {...props}
    />
  );
}

export function DialogTitle({ className, ...props }: DialogTitleProps) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn("font-medium text-text-dark-max p-4", className)}
      {...props}
    />
  );
}

export function DialogDescription({
  className,
  ...props
}: DialogDescriptionProps) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn("text-text-dark-low text-sm", className)}
      {...props}
    />
  );
}
